import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { renameContactFile } from 'api/contacts/single';
import * as Form from 'components/form';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { useForceRender } from 'customHooks/useForceRender';
import { accountQueryKeys } from 'queries/account-query-keys';
import { ContactFile } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

type RenameFileFormValue = Pick<ContactFile, 'name'>;

const RenameFile = ({ children, file }: { children: React.ReactNode; file: ContactFile }) => {
    const { key, forceRender } = useForceRender();
    const { accountId } = useCurrentAccount();
    const toast = useToast();
    const { data: contact } = useCurrentContact();
    const queryClient = useQueryClient();

    const methods = Form.useGivebutterForm<RenameFileFormValue>({
        defaultValues: {
            name: file.name,
        },
    });
    const { control, handleSubmit, setError, resetFormButton, reset } = methods;

    const { mutate: renameFile, isPending: isLoading } = useMutation({
        mutationFn: (formValue: RenameFileFormValue) => {
            return renameContactFile(accountId, contact.id, { id: file.id, ...formValue });
        },
        onSuccess: () => {
            toast.success('Successfully renamed file');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.contactFiles(accountId, contact.id),
            });
            forceRender();
        },
        onError: (e: GivebutterAxiosError) => {
            const data = e.response?.data;
            const errors = data?.errors;

            toast.error(data?.message ?? 'Failed to update contact file');

            if (typeof errors === 'object') {
                for (let field in errors) {
                    setError(field as keyof RenameFileFormValue, {
                        message: errors[field][0],
                    });
                }
            }
            resetFormButton();
        },
    });

    const onSubmit = (formValue: RenameFileFormValue) => {
        renameFile(formValue);
    };

    return (
        <AlertDialog.Root key={key} type={'info'} onOpenChange={open => !open && reset()}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <Form.Provider {...methods}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <AlertDialog.Body>
                                <AlertDialog.Title>Rename file</AlertDialog.Title>
                                <AlertDialog.Description>
                                    <Form.Container>
                                        <Form.Label name="name" htmlFor="name">
                                            File name
                                        </Form.Label>
                                        <Form.TextInputRoot control={control} name="name">
                                            <Form.TextInputForm />
                                        </Form.TextInputRoot>
                                        <Form.Error name="name" />
                                    </Form.Container>
                                </AlertDialog.Description>
                            </AlertDialog.Body>
                            <AlertDialog.Footer>
                                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                <Form.SubmitButton
                                    className="flex-1"
                                    type="submit"
                                    disabled={isLoading}
                                >
                                    Save
                                </Form.SubmitButton>
                            </AlertDialog.Footer>
                        </form>
                    </Form.Provider>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const Root = RenameFile;
const Trigger = AlertDialog.Trigger;
const RenameFileTrigger = Trigger;

export {
    Root,
    Trigger,
    //
    RenameFile,
    RenameFileTrigger,
};
